<template>
    <properties-get
        :page="1"
        :per-page="1"
        :archived="false"
        v-slot="{
            meta,
            error: get_error,
            has_loaded,
            get
        }"
        @success="onPropertiesGetSuccess"
    >
        <template v-if="!has_loaded || get_error">
            <div>
                <base-loading
                    :error="get_error"
                    :retry="get"
                    class="py-6"
                />

                <error-modal
                    v-if="get_error"
                    :error="get_error"
                />
            </div>
        </template>

        <template v-else>
            <tenancies-store
                v-slot="{
                    sid,
                    is_loading: is_saving,
                    data: tenancy,
                    error,
                    store,
                    forget
                }"
                @success="onTenanciesStoreSuccess"
                @error="onTenanciesStoreError"
            >
                <div class="bg-white rounded shadow">
                    <header class="flex items-center px-4 py-3 border-b sm:px-6 md:py-4">
                        <div class="flex-grow">
                            <h1 class="text-lg font-normal">
                                Create a tenancy
                            </h1>
                        </div>

                        <div class="w-6 h-6">
                            <a
                                href="https://help.rentora.co.uk/article/42-how-do-i-create-a-tenancy"
                                target="_blank"
                                rel="noopener"
                            >
                                <base-svg
                                    name="icon-help-circle"
                                    class="w-full h-full"
                                />
                            </a>
                        </div>
                    </header>

                    <base-form @submit="store(form)">
                        <div class="px-4 pt-6 border-b sm:px-6">
                            <validator-alert :sid="sid" />

                            <form-section>
                                <template slot="title">Property</template>
                                <template slot="description">
                                    Which property are you letting?
                                </template>

                                <grid-layout>
                                    <grid-layout-item v-if="meta.total === 0">
                                        <form-items-address
                                            v-model="form.property.address"
                                            :sid="sid"
                                            :disabled="is_saving"
                                            validator-name="property.address"
                                        />
                                    </grid-layout-item>

                                    <grid-layout-item
                                        v-else
                                        class="md:w-1/2"
                                    >
                                        <form-item
                                            :sid="sid"
                                            :is-required="true"
                                            name="property"
                                        >
                                            <template slot="label">Property</template>

                                            <input-property
                                                v-model="form.property"
                                                :uuid="_.get(tenancy, 'property.uuid', $route.query.property)"
                                                :sid="sid"
                                                :disabled="is_saving"
                                                name="property"
                                            />

                                            <template slot="hint">
                                                Do you need to
                                                <router-link
                                                    :to="{
                                                        name: $router.fix('dashboard.*.properties.create'),
                                                        query: {
                                                            then: 'create-tenancy'
                                                        }
                                                    }"
                                                    class="text-blue"
                                                >
                                                    create a property
                                                </router-link>
                                                first?
                                            </template>
                                        </form-item>
                                    </grid-layout-item>
                                </grid-layout>
                            </form-section>
                        </div>

                        <div class="px-4 pt-6 border-b sm:px-6">
                            <form-section>
                                <template slot="title">Period</template>
                                <template slot="description">
                                    How long are you letting the property for?
                                </template>

                                <grid-layout>
                                    <grid-layout-item class="md:w-1/2">
                                        <form-item
                                            :sid="sid"
                                            :is-required="true"
                                            name="term"
                                        >
                                            <template slot="label">Term</template>

                                            <input-select
                                                v-model="form.term"
                                                :sid="sid"
                                                :disabled="is_saving"
                                                :options="[
                                                    { value: 'fixed', label: 'Fixed' },
                                                    { value: 'periodic', label: 'Periodic' }
                                                ]"
                                                name="term"
                                            />
                                        </form-item>
                                    </grid-layout-item>

                                    <grid-layout-item class="md:w-1/2">
                                        <form-item
                                            :sid="sid"
                                            :is-required="true"
                                            name="starts_at"
                                        >
                                            <template slot="label">Starts</template>

                                            <input-date
                                                v-model="form.starts_at"
                                                :sid="sid"
                                                :disabled="is_saving"
                                                name="starts_at"
                                            />
                                        </form-item>
                                    </grid-layout-item>

                                    <template v-if="form.term === 'fixed'">
                                        <grid-layout-item class="md:w-1/2">
                                            <form-item
                                                :sid="sid"
                                                :is-required="true"
                                                name="duration"
                                            >
                                                <template slot="label">Duration</template>

                                                <div class="relative">
                                                    <input-text
                                                        v-model="form.duration"
                                                        type="tel"
                                                        :sid="sid"
                                                        :disabled="is_saving"
                                                        name="duration"
                                                        class="pr-20"
                                                    />
                                                    <div class="absolute inset-y-0 right-0 flex items-center px-3 pt-1 margin-px text-grey-darker">
                                                        months
                                                    </div>
                                                </div>
                                            </form-item>
                                        </grid-layout-item>

                                        <grid-layout-item class="md:w-1/2">
                                            <form-item
                                                :sid="sid"
                                                :is-required="true"
                                                name="is_rolling"
                                            >
                                                <template slot="label">Periodic</template>

                                                <template slot="help">
                                                    After the fixed term ends, the term is switched to periodic. The tenancy will then
                                                    continue on a month-to-month basis.
                                                </template>

                                                <div class="flex items-center">
                                                    <input-checkbox
                                                        v-model="form.is_rolling"
                                                        :sid="sid"
                                                        :disabled="is_saving"
                                                        name="is_rolling"
                                                    />

                                                    <div class="ml-3 leading-none mt-2px">Roll onto periodic</div>
                                                </div>
                                            </form-item>
                                        </grid-layout-item>
                                    </template>
                                </grid-layout>
                            </form-section>
                        </div>

                        <div class="px-4 pt-6 border-b sm:px-6">
                            <form-section>
                                <template slot="title">Rent</template>
                                <template slot="description">
                                    How much is the rent, and when do you want to collect it?
                                </template>

                                <grid-layout>
                                    <grid-layout-item class="md:w-1/2">
                                        <form-item
                                            :sid="sid"
                                            :is-required="true"
                                            name="amount"
                                        >
                                            <template slot="label">Monthly Rent</template>

                                            <div class="relative">
                                                <input-text
                                                    v-model="form.amount"
                                                    :sid="sid"
                                                    :disabled="is_saving"
                                                    type="tel"
                                                    name="amount"
                                                    class="pl-7"
                                                />

                                                <div class="absolute inset-y-0 left-0 flex items-center px-3 pt-1 margin-px text-grey-darker">
                                                    &pound;
                                                </div>
                                            </div>
                                        </form-item>
                                    </grid-layout-item>

                                    <grid-layout-item class="md:w-1/2">
                                        <form-item
                                            :sid="sid"
                                            :is-required="true"
                                            name="collection"
                                        >
                                            <template slot="label">Collection</template>

                                            <input-select
                                                v-model="form.collection"
                                                :sid="sid"
                                                :disabled="is_saving"
                                                :options="[
                                                    { value: 'advance', label: 'Advance' },
                                                    { value: 'arrears', label: 'Arrears' }
                                                ]"
                                                name="collection"
                                            />
                                        </form-item>
                                    </grid-layout-item>
                                </grid-layout>
                            </form-section>
                        </div>

                        <div class="px-4 py-3 text-right bg-grey-bg sm:px-6">
                            <base-button
                                :is-loading="is_saving"
                                class="w-full btn-blue md:w-auto"
                            >
                                Create Tenancy
                            </base-button>
                        </div>

                        <error-modal
                            v-if="error && $error.is(error, { exclude: [422] })"
                            :error="error"
                        />

                        <modal-success
                            :is-open="show_created_modal"
                            @close="show_created_modal = false; forget()"
                        >
                            <template slot="heading">Tenancy created!</template>

                            <p>
                                Your tenancy has been created and is ready for you to begin
                                adding tenants.
                            </p>

                            <div
                                slot="footer"
                                slot-scope="{ close }"
                                class="flex flex-col md:flex-row-reverse"
                            >
                                <router-link
                                    :to="{
                                        name: $router.fix('dashboard.*.tenancies.show'),
                                        params: {
                                            tenancy: tenancy.uuid
                                        }
                                    }"
                                    class="text-center btn btn-green"
                                >
                                    Go to Tenancy
                                </router-link>

                                <button
                                    type="button"
                                    @click.prevent="close"
                                    class="mt-3 text-center btn md:mt-0 md:mr-3"
                                >
                                    Add another
                                </button>
                            </div>
                        </modal-success>
                    </base-form>
                </div>
            </tenancies-store>
        </template>
    </properties-get>
</template>

<script>
    import moment from 'moment'
    import FormItemsAddress from '../../../../components/form-items-address'
    import InputProperty from '../../../../components/input-property'
    import PropertiesGet from '../../../../components/properties-get'
    import TenanciesStore from '../../../../components/tenancies-store'

    export default {
        components: {
            FormItemsAddress,
            InputProperty,
            PropertiesGet,
            TenanciesStore
        },
        data () {
            return {
                show_created_modal: false,
                form: {
                    property: null,
                    term: 'fixed',
                    starts_at: moment().format('DD/MM/YYYY'),
                    duration: '12',
                    is_rolling: true,
                    amount: null,
                    collection: 'advance'
                }
            }
        },
        methods: {
            onPropertiesGetSuccess ({ meta }) {
                if (meta.total === 0) {
                    this.form.property = {
                        address: {
                            street: '',
                            locality: '',
                            village: '',
                            town: '',
                            county: '',
                            postcode: ''
                        }
                    }
                }
            },
            onTenanciesStoreSuccess () {
                this.$track(
                    'dashboard.landlords.tenancies.create',
                    'form-submission',
                    'A landlord has created a tenancy'
                )

                setTimeout(() => {
                    this.show_created_modal = true
                }, 0)
            },
            onTenanciesStoreError () {
            }
        }
    }
</script>
